import { Link } from 'react-router-dom';
import { ArrowUpRight } from 'lucide-react';
import { useCollection } from '@/lib/siteData';
import { cn } from '@/lib/utils';
import type { OfferRow } from '@/schemas';

interface OffersProps {
  index?: number;
  label?: string;
  heading?: string;
  intro?: string;
  /**
   * Section background. Sections alternate gray/elevated down a page; the
   * homepage places this immediately after AudiencePaths, which is also gray,
   * so without an override the two read as one undifferentiated slab.
   */
  surface?: 'gray' | 'elevated';
  /**
   * Cap how many offers render (by sortOrder). When the full list is longer,
   * a "See all engagement models" link to /services renders below the grid.
   * Home passes a small limit so the mobile homepage isn't ~19,000px tall;
   * /services omits it and keeps the full list.
   */
  limit?: number;
}

export function Offers({
  index,
  label = 'ENGAGEMENT MODELS',
  heading = 'How to work with me.',
  intro = 'Five productized offers — each with a clear purpose, output list, and CTA. Pick the one that fits where you are.',
  surface = 'gray',
  limit,
}: OffersProps) {
  const { data: rows } = useCollection<OfferRow>('offers');
  const allOffers = rows.filter((o) => o.visible !== false).sort((a, b) => a.sortOrder - b.sortOrder);
  const offers = limit != null ? allOffers.slice(0, limit) : allOffers;
  const truncated = offers.length < allOffers.length;

  if (offers.length === 0) return null;

  return (
    <section
      className={cn(
        'w-full py-20 md:py-28 border-t border-surface-border-gray',
        surface === 'elevated' ? 'bg-surface-elevated' : 'bg-surface-gray',
      )}
    >
      <div className="w-full max-w-[1200px] mx-auto px-6 lg:px-8">
        <div className="max-w-3xl mb-12">
          <span className="font-mono-label text-xs uppercase tracking-[0.2em] text-text-light">
            {index != null && <span className="text-accent-blue mr-1">{String(index).padStart(2, '0')} — </span>}
            {label}
          </span>
          {heading && (
            <h2 className="font-geist-mono text-3xl md:text-[2.5rem] font-medium text-text-primary mt-3 leading-[1.15] tracking-tight">
              {heading}
            </h2>
          )}
          {intro && (
            <p className="font-body text-base text-text-gray mt-4 leading-relaxed max-w-2xl">
              {intro}
            </p>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {offers.map((offer) => {
            const outputs = offer.outputs;
            return (
              <article
                key={offer.slug}
                className="flex flex-col p-6 rounded-xl border border-surface-border-gray bg-surface-elevated hover:border-text-light/40 transition-colors duration-150"
              >
                {offer.duration && (
                  <span className="font-mono-label text-[10px] uppercase tracking-wider text-accent-blue mb-2">
                    {offer.duration}
                  </span>
                )}
                <h3 className="font-heading text-lg font-semibold text-text-primary leading-snug mb-3">
                  {offer.title}
                </h3>
                <p className="font-body text-sm text-text-gray leading-relaxed mb-4">
                  {offer.purpose}
                </p>
                {offer.bestFor && (
                  <p className="font-body text-xs text-text-light mb-4 leading-relaxed">
                    <span className="font-medium text-text-gray">Best for:</span> {offer.bestFor}
                  </p>
                )}
                {outputs.length > 0 && (
                  <ul className="space-y-1.5 mb-5">
                    {outputs.map((out, i) => (
                      <li
                        key={i}
                        className="font-body text-xs text-text-gray leading-relaxed pl-3 relative before:content-[''] before:absolute before:left-0 before:top-[0.5em] before:w-1 before:h-1 before:rounded-full before:bg-accent-blue/70"
                      >
                        {out}
                      </li>
                    ))}
                  </ul>
                )}
                {/*
                  Exclusions and engagement model come from the spec's
                  `service_card_required_fields`. They sit BELOW the outputs and
                  above the CTA deliberately: a reader who has got this far is
                  weighing the offer, and this is where self-disqualification is
                  cheapest for both sides.
                */}
                {(offer.exclusions?.length ?? 0) > 0 && (
                  <p className="font-body text-xs text-text-light leading-relaxed mb-3">
                    <span className="font-medium text-text-gray">Not included: </span>
                    {offer.exclusions!.join(' ')}
                  </p>
                )}
                {offer.engagementModel && (
                  <p className="font-body text-xs text-text-light leading-relaxed mb-4">
                    <span className="font-medium text-text-gray">Engagement: </span>
                    {offer.engagementModel}
                  </p>
                )}
                {offer.ctaLabel && offer.ctaHref && (
                  <Link
                    to={offer.ctaHref}
                    className="inline-flex items-center gap-1 mt-auto font-body text-sm font-medium text-accent-blue hover:underline"
                  >
                    {offer.ctaLabel}
                    <ArrowUpRight className="w-3.5 h-3.5" />
                  </Link>
                )}
              </article>
            );
          })}
        </div>

        {truncated && (
          <div className="mt-10">
            <Link
              to="/services"
              className="inline-flex items-center gap-1.5 rounded-lg border border-accent-blue/40 bg-accent-blue/5 px-5 py-2.5 font-body text-sm font-medium text-accent-blue transition-colors hover:bg-accent-blue hover:text-accent-fg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-blue"
            >
              See all engagement models
              <ArrowUpRight className="w-4 h-4" />
            </Link>
          </div>
        )}
      </div>
    </section>
  );
}
